import { administrationApiRtkQuery } from '@chillibean/administration-api-client-sdk';
import {
  ButtonSize,
  ButtonType,
  Card,
  DropdownWithOptions,
  IconButton,
  List,
  SortableCard,
  SortableList,
  icons,
  sizing,
  getTestAttributes,
  slug,
} from '@chillibean/component-library';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectInternalResult } from '../../redux/internal.slice';

const reorderedArray = (array, from, to) => {
  const newArray = array.slice();
  const selectedItem = newArray.splice(from, 1)[0];
  newArray.splice(to, 0, selectedItem);

  return newArray;
};

function ChapterDefinitionCardsContainer(props) {
  const { chapters = [], dropdownOptions, handleChapterClick = undefined, loading = true } = props;

  // HOOKS
  // React
  const [cards, setCards] = useState([]);
  const [activeId, setActiveId] = useState(null);
  // Store
  const { accountId, authenticityToken } = useSelector(selectInternalResult);
  // Custom
  const { useUpdateChapterDefinitionsPositionsMutation } = administrationApiRtkQuery;
  const [reorderChapterDefinitions] = useUpdateChapterDefinitionsPositionsMutation(
    { accountId: accountId },
    { skip: !accountId },
  );

  useEffect(() => {
    setCards(chapters);
  }, [chapters.length]);

  const sortedChapters = useMemo(() => {
    if (!cards) return [];
    return cards.slice().sort((a, b) => a.position - b.position);
  }, [cards]);

  const activeCard = useMemo(() => {
    return cards.find(({ id }) => id === activeId);
  }, [activeId]);

  // METHODS
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handlePositionsChanged = async (values) => {
    await reorderChapterDefinitions({
      accountId: accountId,
      body: { ...values, authenticityToken: authenticityToken },
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    const getMovedCards = () => {
      const activeIndex = cards.findIndex(({ id }) => id === active.id);
      const overIndex = cards.findIndex(({ id }) => id === over.id);
      const movedItems = reorderedArray(_.cloneDeep(cards), activeIndex, overIndex);
      for (let i = 0; i < movedItems.length; i++) {
        movedItems[i].position = i + 1;
      }
      return movedItems;
    };

    if (active.id !== over.id) {
      const movedCards = getMovedCards();
      setCards(movedCards);
      handlePositionsChanged({
        chapter_definitions: movedCards.map((card) => ({
          id: card.id,
          position: card.position,
        })),
      });
    }

    setActiveId(null);
  };

  const handleClick = (data) => {
    handleChapterClick(data);
  };

  const getSortableOverlayChildren = () => {
    return (
      activeCard && (
        <SortableCard grabbable key={activeId} id={activeId} title={activeCard.name}>
          <StyledContainer>
            <StyledContainer>
              <StyledColorIndicator color={activeCard.color} {...getTestAttributes('chapter-definition-color')} />
              <DropdownWithOptions items={dropdownOptions(activeId)}>
                <IconButton icon={icons.baselineMoreVert} size={ButtonSize.SLIM} type={ButtonType.TERTIARY} />
              </DropdownWithOptions>
            </StyledContainer>
          </StyledContainer>
        </SortableCard>
      )
    );
  };

  if (loading) {
    return (
      <List>
        {[...Array(4).keys()].map((key) => (
          <Card key={key} loading />
        ))}
      </List>
    );
  }

  return (
    <List>
      <SortableList
        getOverlayElement={getSortableOverlayChildren}
        items={sortedChapters}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        {sortedChapters.map((definition) => (
          <SortableCard
            grabbable
            key={definition.id}
            id={definition.id}
            title={definition.name}
            onClick={() => handleClick(definition)}
          >
            <StyledContainer {...getTestAttributes(`chapter-definition-${definition.id}`)}>
              <StyledContainer>
                <StyledColorIndicator $color={definition.color} {...getTestAttributes('chapter-definition-color')} />
                <DropdownWithOptions items={dropdownOptions(definition)}>
                  <IconButton
                    icon={icons.baselineMoreVert}
                    size={ButtonSize.SLIM}
                    type={ButtonType.TERTIARY}
                    {...getTestAttributes(`kebab-${slug(definition.name)}`)}
                  />
                </DropdownWithOptions>
              </StyledContainer>
            </StyledContainer>
          </SortableCard>
        ))}
      </SortableList>
    </List>
  );
}

const StyledColorIndicator = styled.div`
  width: 40px;
  height: ${sizing.medium};
  border-radius: ${sizing.smedium};
  background-color: ${(props) => props.$color};
`;

const StyledContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${sizing.medium};
`;

ChapterDefinitionCardsContainer.propTypes = {
  chapters: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      color: PropTypes.string,
      position: PropTypes.number,
    }),
  ),
  dropdownOptions: PropTypes.func.isRequired,
  handleChapterClick: PropTypes.func,
  loading: PropTypes.bool,
};

export default ChapterDefinitionCardsContainer;
